import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale
} from 'chart.js';
import { fetchMedicationGroupList, fetchInfusionStats } from '../../api/reportService';
import { fetchPatients } from '../../api/patientService';

ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale
);

const Medications = () => {
  const [activeIndex, setActiveIndex] = useState(-1);
  const chartRef = useRef(null);

  // Medication color mapping - assign unique color to each medication by name
  const medicationColors = {
    'Haegarda-2000': { bg: '#90E0EF', hover: '#7AD4E3' },
    'Haegarda-3000': { bg: '#FF6B6B', hover: '#FF5252' },
    'Cinryze': { bg: '#FFB4A2', hover: '#FFA088' },
    'Firazyr': { bg: '#B5E48C', hover: '#9ED670' },
    'Berinert-500': { bg: '#CDB4DB', hover: '#BFA4CF' },
    'Berinert-1500': { bg: '#4ECDC4', hover: '#3DB8AF' },
    'Takhzyro': { bg: '#D9D9D9', hover: '#CCCCCC' },
    'Icatibant': { bg: '#FFE066', hover: '#FFD84D' },
    'Orladeyo': { bg: '#FFCCD5', hover: '#FFBBBB' },
  };

  // Helper function to get color for a medication
  const getMedicationColor = (medicationName, type = 'bg') => {
    const normalizedName = medicationName.trim();
    if (medicationColors[normalizedName]) {
      return medicationColors[normalizedName][type];
    }
    // Default color for unknown medications
    return type === 'bg' ? '#95A5A6' : '#7F8C8D';
  };

  // Calculate default date range (1 month from today)
  const getDefaultDateRange = () => {
    const today = new Date();
    const oneMonthAgo = new Date(today);
    oneMonthAgo.setMonth(today.getMonth() - 1);

    return {
      start: oneMonthAgo.toISOString().split('T')[0],
      end: today.toISOString().split('T')[0]
    };
  };

  const defaultDates = getDefaultDateRange();
  const [startDate, setStartDate] = useState(defaultDates.start);
  const [endDate, setEndDate] = useState(defaultDates.end);
  const [medications, setMedications] = useState([]);
  const [medicationsLoading, setMedicationsLoading] = useState(false);
  const [medicationsError, setMedicationsError] = useState(null);
  const [infusionData, setInfusionData] = useState([]);
  const [infusionLoading, setInfusionLoading] = useState(false);
  const [totalPatients, setTotalPatients] = useState(0);

  // Patient filter state
  const [patients, setPatients] = useState([]);
  const [selectedPatients, setSelectedPatients] = useState([]);
  const [showPatientDropdown, setShowPatientDropdown] = useState(false);

  // Load medication list from API
  const loadMedicationList = useCallback(async () => {
    try {
      setMedicationsLoading(true);
      setMedicationsError(null);
      console.log('[Medications] Fetching medication list from API');

      const data = await fetchMedicationGroupList();

      // Transform API response to component state format
      const medicationList = data.map((med, index) => ({
        id: index + 1,
        name: med._id.toUpperCase(), // Convert to uppercase for display
        checked: true,
        total: med.total
      }));

      setMedications(medicationList);
      console.log('[Medications] Loaded medications:', medicationList);
    } catch (err) {
      console.error('Error loading medication list:', err);
      setMedicationsError(err.message);
      // Fallback to default medications if API fails
      setMedications([
        { id: 1, name: 'HAEGARDA-2000', checked: true },
        { id: 2, name: 'HAEGARDA-3000', checked: true },
        { id: 3, name: 'BERINERT-500', checked: true },
        { id: 4, name: 'BERINERT-1500', checked: true },
        { id: 5, name: 'CINRYZE', checked: true },
        { id: 6, name: 'TAKHZYRO', checked: true },
        { id: 7, name: 'FIRAZYR', checked: true },
        { id: 8, name: 'ORLADEYO', checked: true },
      ]);
    } finally {
      setMedicationsLoading(false);
    }
  }, []);

  // Load medications on component mount
  useEffect(() => {
    loadMedicationList();
  }, [loadMedicationList]);

  // Load patients list for dropdown filter
  useEffect(() => {
    const loadPatients = async () => {
      try {
        const result = await fetchPatients({ all: true });
        setPatients(result.data);
        console.log(`[Medications] Loaded ${result.data.length} patients for dropdown`);
      } catch (err) {
        console.error('[Medications] Failed to load patients:', err);
      }
    };
    loadPatients();
  }, []);

  // Load infusion statistics from API
  const loadInfusionStats = useCallback(async () => {
    try {
      setInfusionLoading(true);
      console.log('[Medications] Fetching infusion stats');

      // Get checked medication names in lowercase with dosage format
      const selectedMeds = medications
        .filter(med => med.checked)
        .map(med => med.name.toLowerCase());

      const data = await fetchInfusionStats({
        startDate,
        endDate,
        medications: selectedMeds.length > 0 ? selectedMeds : undefined,
        patientIds: selectedPatients.length > 0 ? selectedPatients : undefined
      });

      setTotalPatients(data.totalPatients || 0);

      // Calculate total unique patients across ALL medications in the filtered date range
      // Collect all unique patient IDs from all medication results
      const allUniquePatientIds = new Set();
      data.results.forEach(item => {
        if (item.uniquePatientIds && Array.isArray(item.uniquePatientIds)) {
          item.uniquePatientIds.forEach(patientId => allUniquePatientIds.add(patientId));
        }
      });
      const totalUniquePatientsInDateRange = allUniquePatientIds.size;

      console.log('[Medications] Total unique patients in date range:', totalUniquePatientsInDateRange);
      console.log('[Medications] Total consented patients (system):', data.totalPatients);

      // Transform API response to component format
      const transformed = data.results.map(item => {
        const patientsCount = item.uniquePatientIds.length;
        // Use total unique patients who had events in date range, not total consented patients
        const totalPatientsNum = totalUniquePatientsInDateRange || 1;
        const percentage = ((patientsCount / totalPatientsNum) * 100).toFixed(2);

        // Debug log to check API response structure
        console.log('[Medications] Item data:', {
          id: item._id,
          total: item.total,
          doses: item.doses,
          attackCount: item.attackCount,
          patientsForThisMed: patientsCount,
          totalPatientsInDateRange: totalPatientsNum,
          calculatedPercentage: percentage
        });

        return {
          name: item._id.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join('-'),
          patients: patientsCount,
          percentage: `${percentage}%`,
          // Use 'total' field for count of infusions (Total Number of Infusions column)
          infusions: item.total || 0,
          // Store total doses separately if needed
          totalDoses: item.doses || 0,
          attacks: item.attackCount || 0
        };
      });

      setInfusionData(transformed);
      console.log('[Medications] Loaded infusion data:', transformed);
    } catch (err) {
      console.error('Error loading infusion stats:', err);
      // Keep existing data on error
    } finally {
      setInfusionLoading(false);
    }
  }, [startDate, endDate, medications, selectedPatients]);

  // Load infusion stats when medications or date range changes
  useEffect(() => {
    const hasCheckedMedications = medications.some(med => med.checked);
    if (hasCheckedMedications) {
      loadInfusionStats();
    } else {
      // Clear data when no medications are selected
      setInfusionData([]);
    }
  }, [medications, startDate, endDate, loadInfusionStats]);

  // Check if any medications are selected
  const hasSelectedMedications = medications.some(med => med.checked);

  // Check if the data is meaningful (not fallback/empty data)
  const hasRealData = infusionData.length > 0 && infusionData.some(med =>
    med.infusions > 0 || med.attacks > 0
  );

  // Use API data only if medications are selected AND there's real data
  const medicationData = hasSelectedMedications && hasRealData ? infusionData : [];

  const handleCheckboxChange = (id) => {
    setMedications(medications.map(med =>
      med.id === id ? { ...med, checked: !med.checked } : med
    ));
  };

  // Patient dropdown helpers
  const uniquePatients = patients.map(patient => ({
    id: patient._id,
    name: `${patient.otherDetail?.name || patient.name || 'Unknown'} (#${patient.patientNumber})`,
    patientNumber: patient.patientNumber
  }));

  const handlePatientSelection = (patientId) => {
    setSelectedPatients(prev =>
      prev.includes(patientId)
        ? prev.filter(id => id !== patientId)
        : [...prev, patientId]
    );
  };

  const clearPatientFilter = () => {
    setSelectedPatients([]);
  };

  // Calculate total infusions for percentage calculation
  const totalInfusions = medicationData.reduce((sum, med) => sum + med.infusions, 0);

  const handleChartClick = (event) => {
    if (!chartRef.current) return;

    const chart = chartRef.current;
    const elements = chart.getElementsAtEventForMode(event, 'nearest', { intersect: true }, false);
    
    if (elements.length > 0) {
      const clickedIndex = elements[0].index;
      setActiveIndex(prevIndex => clickedIndex === prevIndex ? -1 : clickedIndex);
    }
  };

  const infusionsChartData = {
    labels: medicationData.map(med => med.name),
    datasets: [{
      data: medicationData.map(med => med.infusions),
      backgroundColor: medicationData.map(med => getMedicationColor(med.name, 'bg')),
      hoverBackgroundColor: medicationData.map(med => getMedicationColor(med.name, 'hover')),
      borderWidth: 0,
      spacing: 0,
      hoverOffset: 20
    }]
  };

  const infusionsChartOptions = {
    plugins: {
      legend: {
        position: 'right',
        labels: {
          usePointStyle: true,
          padding: 20,
          font: {
            size: 12
          }
        }
      },
      tooltip: {
        enabled: true,
        position: 'nearest',
        yAlign: (context) => {
          if (!context.tooltip || !context.tooltip.dataPoints || context.tooltip.dataPoints.length === 0) return 'center';
          
          // Get the angle of the current segment's midpoint
          const dataPoint = context.tooltip.dataPoints[0];
          const meta = context.chart.getDatasetMeta(dataPoint.datasetIndex);
          const arc = meta.data[dataPoint.dataIndex];
          
          // Calculate the midpoint angle of the segment
          const midAngle = (arc.startAngle + arc.endAngle) / 2;
          
          // Convert angle to degrees and normalize to 0-360
          const angle = ((midAngle * 180) / Math.PI) % 360;
          
          // Determine position based on angle
          if (angle >= 270 || angle <= 90) {
            return 'bottom';
          } else {
            return 'top';
          }
        },
        xAlign: 'center',
        callbacks: {
          label: (context) => {
            const value = context.raw;
            const percentage = ((value / totalInfusions) * 100).toFixed(2);
            return `${context.label}: ${value} (${percentage}%)`;
          }
        },
        padding: 12,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleFont: {
          size: 12,
          family: "'Mulish', sans-serif"
        },
        bodyFont: {
          size: 12,
          family: "'Mulish', sans-serif"
        },
        displayColors: false,
        caretSize: 8,
        caretPadding: 10
      }
    },
    cutout: '60%',
    radius: '85%',
    maintainAspectRatio: false,
    responsive: true,
    animation: {
      animateRotate: false,
      animateScale: true,
      duration: 800,
      easing: 'easeOutQuart'
    },
    elements: {
      arc: {
        borderWidth: 0
      }
    },
    onHover: (event, elements) => {
      if (elements.length > 0) {
        event.native.target.style.cursor = 'pointer';
      } else {
        event.native.target.style.cursor = 'default';
      }
    }
  };

  const [activePatientIndex, setActivePatientIndex] = useState(-1);
  const patientsChartRef = useRef(null);

  const patientsChartData = {
    labels: medicationData.map(med => med.name),
    datasets: [{
      data: medicationData.map(med => parseFloat(med.percentage)),
      backgroundColor: medicationData.map(med => getMedicationColor(med.name, 'bg')),
      hoverBackgroundColor: medicationData.map(med => getMedicationColor(med.name, 'hover')),
      borderWidth: 0,
      spacing: 0,
      hoverOffset: 20
    }]
  };

  const handlePatientsChartClick = (event) => {
    if (!patientsChartRef.current) return;

    const chart = patientsChartRef.current;
    const elements = chart.getElementsAtEventForMode(event, 'nearest', { intersect: true }, false);
    
    if (elements.length > 0) {
      const clickedIndex = elements[0].index;
      setActivePatientIndex(prevIndex => clickedIndex === prevIndex ? -1 : clickedIndex);
    }
  };

  const patientsChartOptions = {
    plugins: {
      legend: {
        position: 'right',
        labels: {
          usePointStyle: true,
          padding: 20,
          font: {
            size: 12
          }
        }
      },
      tooltip: {
        enabled: true,
        position: 'nearest',
        yAlign: (context) => {
          if (!context.tooltip || !context.tooltip.dataPoints || context.tooltip.dataPoints.length === 0) return 'center';
          
          const dataPoint = context.tooltip.dataPoints[0];
          const meta = context.chart.getDatasetMeta(dataPoint.datasetIndex);
          const arc = meta.data[dataPoint.dataIndex];
          
          const midAngle = (arc.startAngle + arc.endAngle) / 2;
          const angle = ((midAngle * 180) / Math.PI) % 360;
          
          if (angle >= 270 || angle <= 90) {
            return 'bottom';
          } else {
            return 'top';
          }
        },
        xAlign: 'center',
        callbacks: {
          label: (context) => {
            return `${context.label}: ${parseFloat(context.raw).toFixed(2)}%`;
          }
        },
        padding: 12,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleFont: {
          size: 12,
          family: "'Mulish', sans-serif"
        },
        bodyFont: {
          size: 12,
          family: "'Mulish', sans-serif"
        },
        displayColors: false,
        caretSize: 8,
        caretPadding: 10
      }
    },
    cutout: '60%',
    radius: '85%',
    maintainAspectRatio: false,
    responsive: true,
    animation: {
      animateRotate: false,
      animateScale: true,
      duration: 800,
      easing: 'easeOutQuart'
    },
    elements: {
      arc: {
        borderWidth: 0
      }
    },
    onHover: (event, elements) => {
      if (elements.length > 0) {
        event.native.target.style.cursor = 'pointer';
      } else {
        event.native.target.style.cursor = 'default';
      }
    }
  };

  return (
    <div className="p-6">
      {/* Header Section */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-primary">Medications Overview</h1>
          <p className="text-neutral">Track medication usage and patient statistics</p>
        </div>
        <div className="flex gap-4 items-center">
          <div className="flex items-center gap-2">
            <span className="text-neutral">Start Date:</span>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              max={endDate}
              className="border border-light rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-neutral">End Date:</span>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              min={startDate}
              className="border border-light rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
          {/* Patient Filter Dropdown */}
          <div className="relative">
            <button
              type="button"
              onClick={() => setShowPatientDropdown(!showPatientDropdown)}
              className="border border-light rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary text-left flex items-center gap-2 bg-white min-w-[180px]"
            >
              <span className="text-neutral">
                {selectedPatients.length === 0
                  ? 'All Patients'
                  : `${selectedPatients.length} Patient${selectedPatients.length === 1 ? '' : 's'}`}
              </span>
              <i className={`fas fa-chevron-${showPatientDropdown ? 'up' : 'down'} text-neutral ml-auto`}></i>
            </button>

            {showPatientDropdown && (
              <div className="absolute z-50 right-0 w-72 mt-1 bg-white border border-light rounded-lg shadow-lg max-h-60 overflow-y-auto">
                {selectedPatients.length > 0 && (
                  <div className="px-4 py-2 border-b border-light">
                    <button
                      onClick={clearPatientFilter}
                      className="text-sm text-primary hover:text-primary-dark"
                    >
                      Clear selection
                    </button>
                  </div>
                )}
                {uniquePatients.map(patient => (
                  <div
                    key={patient.id}
                    className="flex items-center px-4 py-2 hover:bg-gray-50 cursor-pointer"
                    onClick={() => handlePatientSelection(patient.id)}
                  >
                    <input
                      type="checkbox"
                      checked={selectedPatients.includes(patient.id)}
                      onChange={() => {}}
                      className="h-4 w-4 text-primary border-light rounded focus:ring-primary"
                    />
                    <span className="ml-3 text-sm text-neutral">{patient.name}</span>
                  </div>
                ))}
                {uniquePatients.length === 0 && (
                  <div className="px-4 py-3 text-sm text-neutral text-center">
                    No patients available
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Medications Filter */}
      <div className="bg-gray-100 p-4 rounded-lg mb-6">
        <h2 className="text-lg font-semibold text-primary mb-3">Medications Type</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {medications.map(med => (
            <div key={med.id} className="flex items-center">
              <input
                type="checkbox"
                id={`med-${med.id}`}
                checked={med.checked}
                onChange={() => handleCheckboxChange(med.id)}
                className="w-4 h-4 text-primary focus:ring-primary border-light rounded accent-[#A8DADC] cursor-pointer"
              />
              <label 
                htmlFor={`med-${med.id}`} 
                className="ml-2 text-neutral cursor-pointer hover:text-primary transition-colors duration-200"
              >
                {med.name}
              </label>
            </div>
          ))}
        </div>
      </div>

      {/* No Data Message or Charts/Table */}
      {medicationData.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <div className="flex flex-col items-center justify-center">
            <svg className="w-16 h-16 text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <h3 className="text-xl font-semibold text-gray-600 mb-2">No Data Available</h3>
            <p className="text-gray-500">
              {!hasSelectedMedications
                ? 'Please select at least one medication to view data'
                : 'No data found for the selected medications and date range'}
            </p>
          </div>
        </div>
      ) : (
        <>
          {/* Charts Section */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div className="bg-white p-6 rounded-lg shadow">
              <h3 className="text-lg font-semibold text-primary mb-4">Number of Infusions</h3>
              <div className="h-[300px] relative">
                <Doughnut
                  ref={chartRef}
                  data={infusionsChartData}
                  options={infusionsChartOptions}
                />
              </div>
            </div>
            <div className="bg-white p-6 rounded-lg shadow">
              <h3 className="text-lg font-semibold text-primary mb-4">% of Patients on Product</h3>
              <div className="h-[300px] relative">
                <Doughnut
                  ref={patientsChartRef}
                  data={patientsChartData}
                  options={patientsChartOptions}
                />
              </div>
            </div>
          </div>

          {/* Data Table */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral uppercase tracking-wider">
                    Medication
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral uppercase tracking-wider">
                    Number Of Patients
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral uppercase tracking-wider">
                    % Patients
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral uppercase tracking-wider">
                    # Of Infusions
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral uppercase tracking-wider">
                    # Of Breakthrough Attacks
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-light">
                {medicationData.map((med, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-primary">{med.name}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-primary">{med.patients}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-primary">{med.percentage}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-primary">{med.infusions}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-primary">{med.attacks}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

export default Medications; 